import { useEffect, useRef, useState } from "react";
import { useSetState } from "ahooks";
import message from "@/plugins/message";
import cloneDeep from "lodash.clonedeep";
import { useStore } from "@/stores";

export enum DialogType {
  ASK = "ask",
  ANSWER = "answer",
}
export enum EDialogStatus {
  INIT = "init",
  CONNECTING = "connecting",
  READY = "ready",
  THINKING = "thinking",
  SPEAKING = "speaking",
}
const projectId = "001e7b906d7d469cb890a3270e6573d0";
const useAgent = ({ onClose }: { onClose?: () => void }) => {
  const rtcInstance = useRef<any>(null);
  const asrRef = useRef<any>(null);
  const chatLastFrame = useRef<any>({});
  const [input, setInput] = useState("");
  const [state, setState] = useSetState<{
    status: EDialogStatus;
    chatList: any[];
    humanSpeaking?: boolean;
    name?: string;
  }>({
    status: EDialogStatus.INIT,
    chatList: [],
    humanSpeaking: false,
    name: "",
  });
  const [inputMode, setInputMode] = useState<"keyboard" | "voice">("keyboard");
  const scrollContainer = useRef<HTMLDivElement>(null);
  /* 对话列表滚动到最底部 */
  const scrollLBToBottom = () => {
    if (!scrollContainer.current) return;
    scrollContainer.current.scrollTop = scrollContainer.current.scrollHeight;
  };
  const signRef = useRef<any>(null);
  const handles = {
    break: () => {
      // 如果虚拟人不在说话，点击直接返回
      if (state.status !== EDialogStatus.SPEAKING) return;
      setState({
        status: EDialogStatus.READY,
      });
      rtcInstance.current.breakTalking();
    },
    stopStream: () => {
      asrRef.current?.stop();
      rtcInstance.current?.endRTC();
      chatLastFrame.current = {};
      setState({
        status: EDialogStatus.INIT,
        chatList: [],
      });
    },
    send: (message: string) => {
      rtcInstance.current.breakTalking();
      setState((pre) => ({
        status: EDialogStatus.THINKING,
        chatList: handles.getChatAsr(pre.chatList, message),
      }));
      rtcInstance.current.ask(message);
    },
    asrEndingPunctuation: (str: string | undefined) => {
      if (!str) return "";
      // 匹配字符串末尾的句号，如果有的话去掉
      return str.replace(/[。]$/, "");
    },
    // asr文本拼接
    getChatAsr: (chatList: any[], text: string) => {
      // 判断数组最后一项是否是asr类text
      const last = chatList?.[chatList?.length - 1];
      let result: any[] = [];
      if (last?.type !== DialogType.ASK) {
        result = [...chatList, { type: DialogType.ASK, content: text }];
      } else {
        last.content = text;
        result = [...chatList];
      }
      return result;
    },
    // 答案文本拼接
    getChatAnswer: ({
      chatList,
      content,
      id,
    }: {
      chatList: any[];
      content: string;
      id: string;
    }) => {
      chatList = cloneDeep(chatList);
      const old = chatList?.find((i) => i?.id === id);
      const oldContent = old?.content;
      if (old) {
        old.content = oldContent + content;
      } else {
        chatList = [...chatList, { type: DialogType.ANSWER, content, id }];
      }
      return [...chatList];
    },
    asrStart: () => {
      // 如果asr正在识别 return
      if (asrRef.current?.recognizing) return;
      asrRef.current?.start(signRef.current);
    },
    asrStop: () => {
      asrRef.current?.stop();
    },
  };
  const initAsr = async (asrConfig: any) => {
    let ASRController: any;
    ASRController = (window as any).AsrSDK;
    ASRController.authUseMicrophone()
      .then((res: any) => {
        console.log("==>授权成功", res);
        // 初始化ASR
        asrRef.current = new ASRController({
          projectId,
          closeProxy: true,
          engine_model_type: "16k_zh_large",
          ...asrConfig,
          // 识别变化时
          onRecognitionResultChange: (res: any) => {
            console.log("识别变化时", res);
            //识别变化中
            const { result } = res || {};
            const text = result?.voice_text_str;
            setState((pre) => ({
              chatList: handles.getChatAsr(
                pre.chatList,
                handles.asrEndingPunctuation(text)
              ),
            }));
          },
          // 一句话结束
          onSentenceEnd: (res: any) => {
            console.log("一句话结束", res);
            const { result } = res || {};
            const text = result?.voice_text_str;
            if (!text) return;
            setState((pre) => ({
              status: EDialogStatus.THINKING,
              chatList: handles.getChatAsr(
                pre.chatList,
                handles.asrEndingPunctuation(text)
              ),
            }));
            rtcInstance.current.breakTalking();
            rtcInstance.current.ask(text);
          },
          // 识别结束
          onRecognitionComplete: (res: any) => {
            console.log("识别结束", res);
          },
          // 识别错误
          onError: (res: any) => {
            console.log("识别失败", res);
            message.warning(res?.message);
          },
        });
      })
      .catch((err: any) => {
        console.log("==>授权失败", err);
      });
  };
  const getSignature = async () => {
    const res = await fetch("/openapi/signature/gen");
    const data = await res.json();
    return data.data;
  };
  const isReady = useRef(false);
  const initRtc = async () => {
    const sign = await getSignature();
    signRef.current = sign;
    setState({
      status: EDialogStatus.CONNECTING,
    });
    rtcInstance.current = new window.RTCInteraction({
      mountClass: `.rtcContainer`,
      showDefaultStaticImage: false,
      signature: sign,
      includeUI: true,
      language: "zh",
      proxy: {
        host: `interactive-virtualhuman.xiaoice.com`,
        protocol: "https",
      },
      projectId,
      onError(errorMessage: any) {
        setState({
          status: EDialogStatus.INIT,
        });
        console.log("=================onError===================>");
        console.log(errorMessage);
        message.warning(errorMessage?.message);
        console.log("<==================onError==================");
        if (errorMessage?.code === 887) {
          onClose?.();
        }
      },
      onInited(res: any) {
        console.log("inited", res);
        // 赋值项目基础信息
        initAsr({
          // engine_model_type: res?.engine_model_type,
          hotword_list: res?.hotword_list,
          projectId,
        });
        setState({
          name: res?.base_info?.title || "",
        });
        rtcInstance.current.startRTC();
      },
      onPlayStream() {
        isReady.current = true;
        setState({
          status: EDialogStatus.READY,
        });
        // handles.asrStart();
        console.log("===============onPlayStream=====================");
      },
      onJoinRoom() {
        console.log("===============onJoinRoom=====================");
      },
      onStopStream() {
        console.log("==================onStopStream==================");
      },
      onTalkStart(TalkRes: any) {
        setState((per) => ({
          status: EDialogStatus.SPEAKING,
          chatList: handles.getChatAnswer({
            chatList: per.chatList,
            id: TalkRes.id,
            content: TalkRes.content,
          }),
          // chunkAns: per.chunkAns + TalkRes.content
        }));
        console.log("==================onTalkStart==================>");
        console.log("TalkRes===>", TalkRes, state.chatList);
        //   console.log('TalkRes===>', TalkRes, state.chatList)
        console.log("<==================onTalkStart==================");
      },
      onTalkEnd(TalkRes: any) {
        chatLastFrame.current[TalkRes.id] = TalkRes.last_fragment;
        if (chatLastFrame.current[TalkRes.id]) {
          setState({
            status: EDialogStatus.READY,
          });
        }
        console.log("====================onTalkEnd================>");
        console.log(TalkRes);
        console.log("<====================onTalkEnd================");
      },
    });
  };

  useEffect(() => {
    scrollLBToBottom();
  }, [state.chatList]);
  return {
    state,
    isReady,
    handles,
    input,
    setInput,
    inputMode,
    setInputMode,
    scrollContainer,
    rtcInstance,
    initRtc,
    asrRef,
  };
};
export default useAgent;
